'use client';

import { Liveline } from '@morfi/chart';
import { useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import { useRef, useState } from 'react';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';

export interface WinnersRowLivelineProps {
  /** Oldest → newest D9 close series (e.g. `snapshot.sparkline7d`). */
  readonly series: readonly string[];
  /** Authoritative current D9 price used when the winner has no trade history. */
  readonly currentPriceD9: string;
  /** Signed 24-hour change in basis points (e.g. `snapshot.change24hBps`). */
  readonly change24hBps: string | null;
}

const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_WEEK: number = 7 * SECONDS_PER_DAY;

// The w-28 winners cell is too small for the default endpoint (6.5 px outer
// dot + pulse ring expanding to 21 px), so the wrapper halves every radius
// via the package's pixel-sizing props and pads the canvas to fit the
// shrunk pulse — top/right/bottom (the left edge has no endpoint). The
// values below mirror the previous half-scale look exactly.
const PULSE_SIZE = 8;
// Reserve MORE than `PULSE_SIZE` so the fully-expanded ring clears the canvas
// edge. At `pad === PULSE_SIZE` the endpoint sits exactly `pulseSize` from the
// edge and the ring's outer rim lands on the boundary → it gets cropped. The
// extra headroom lets the whole ring breathe.
const PULSE_CLEARANCE = 4;
const ENDPOINT_PAD: number = Math.ceil(PULSE_SIZE + PULSE_CLEARANCE);

// LiveChart paints into a canvas, which cannot resolve `var(--…)`. Resolve the
// explicit line colour here so it remains the chart's highest-priority colour.
// Kept local because hoisting to a shared util buys nothing for this callsite.
function resolveCssVar(input: string, fallback: string = input): string {
  if (typeof document === 'undefined') return fallback;
  const match = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(input);
  if (!match) return input;
  const varName = match[1] ?? '';
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (resolved.length > 0) return resolved;
  const inlineFallback = match[2];
  return inlineFallback !== undefined ? inlineFallback.trim() : fallback;
}

/**
 * Minimal-chrome LiveChart preview used in the rightmost cell of the desktop
 * Winners row (Figma node 3682-948 → "last 7 days" column). Wraps the canvas-
 * based `<LiveChart>` from `@morfi/chart` in a fully decorative configuration:
 * no axes, no labels, no current-value price line, no crosshair, no markers,
 * no series toggle. An empty time formatter hides label text but not the
 * canvas baseline, so `timeAxis` and `valueLine` are opted out explicitly. The
 * endpoint pulses (dot + canvas-drawn ring, both inheriting the line color)
 * at half the package defaults via the `dotSize` / `innerDotSize` /
 * `pulseSize` pixel props — the full-size endpoint would dominate the line
 * in a 28×9 cell. Pulse stays on under `prefers-reduced-motion` by
 * deliberate design choice, while `smoothing: 1` still collapses
 * value-smoothing to a single frame.
 *
 * Reads the static 7-day close series (`snapshot.sparkline7d`) and renders
 * a single line whose tone follows the row's 24h change sign.
 *
 * Off-screen rendering is gated by an `IntersectionObserver` (200 px
 * `rootMargin` pre-fetch). The canvas unmounts after leaving that margin so
 * scrolling an infinite feed cannot retain every chart's animation loop.
 * Visible charts keep their pulse and smoothing unchanged.
 */
export function WinnersRowLiveline({
  series,
  currentPriceD9,
  change24hBps,
}: WinnersRowLivelineProps): React.ReactNode {
  const { resolvedTheme } = useTheme();
  const theme: 'light' | 'dark' = resolvedTheme === 'light' ? 'light' : 'dark';
  const reduceMotion = useReducedMotion() === true;

  // Captured ONCE on first render (avoids RC-004 `Date.now()` in render).
  // The 7-day series is daily granularity and frozen at resolution for
  // graduated markets — so a single `lastTime` anchor is correct for the
  // wrapper's lifetime.
  const [lastTime] = useState<number>(() => Math.floor(Date.now() / 1000));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver(containerRef, { rootMargin: '200px' });

  const tone: 'success' | 'destructive' = (change24hBps ?? '0').startsWith('-')
    ? 'destructive'
    : 'success';
  const color = resolveCssVar(tone === 'destructive' ? 'var(--data-loss)' : 'var(--data-profit)');

  const flatValue = Number(currentPriceD9);
  const points =
    series.length < 2
      ? [
          { time: lastTime - SECONDS_PER_WEEK, value: flatValue },
          { time: lastTime, value: flatValue },
        ]
      : series.map((v, i) => ({
          time: lastTime - (series.length - 1 - i) * SECONDS_PER_DAY,
          value: Number(v),
        }));
  const lastValue = points[points.length - 1]?.value ?? 0;
  const windowSecs = series.length < 2 ? SECONDS_PER_WEEK : (series.length - 1) * SECONDS_PER_DAY;

  // Reduced-motion ⇒ collapse smoothing to a single frame.
  // `exactOptionalPropertyTypes`: spread to omit `smoothing` entirely when
  // motion is allowed, instead of passing `undefined`.
  const smoothingProp: { lerpSpeed: number } | Record<string, never> = reduceMotion
    ? { lerpSpeed: 1 }
    : {};

  return (
    <div
      ref={containerRef}
      className="pointer-events-none block h-full w-full"
      aria-hidden
      role="presentation"
    >
      {isIntersecting ? (
        <Liveline
          data={points}
          value={lastValue}
          color={color}
          lineWidth={1.5}
          theme={theme}
          window={windowSecs}
          grid={false}
          timeAxis={false}
          scrub={false}
          valueLine={false}
          pulse
          badge={false}
          momentum={false}
          fill
          {...smoothingProp}
          padding={{ top: ENDPOINT_PAD, right: ENDPOINT_PAD, bottom: ENDPOINT_PAD, left: 4 }}
          className="block h-full w-full"
        />
      ) : null}
    </div>
  );
}
